import { queryOptions, keepPreviousData } from '@tanstack/react-query';
import { getShouldFallback } from '@/lib/echo';
import { STALE_TIME } from '@/lib/query/stale-times';
import { ocrApi } from './ocr.api';
import type { OcrContainersListParams } from './ocr.api';

export const ocrKeys = {
  all: ['ocr'] as const,
  containers: () => [...ocrKeys.all, 'containers'] as const,
  containerList: (filters: OcrContainersListParams) =>
    [...ocrKeys.containers(), filters] as const,
  containerResults: (containerId: number) =>
    [...ocrKeys.all, 'container', containerId] as const,
  // Nested UNDER containerResults on purpose: every mutation that already
  // invalidates the results (start, pause, submit-to-QA, a QA verdict) also
  // changes the header's qa_stage or template pool, and a prefix invalidation
  // catches both. The websocket patcher writes containerResults by exact key,
  // so it never touches this one.
  container: (containerId: number) =>
    [...ocrKeys.containerResults(containerId), 'summary'] as const,
  containerHistory: (containerId: number) =>
    [...ocrKeys.all, 'container', containerId, 'history'] as const,
  result: (resultId: number) => [...ocrKeys.all, 'result', resultId] as const,
  activeJobs: () => [...ocrKeys.all, 'active-jobs'] as const,
  registrationTemplates: (registrationId: number) =>
    [...ocrKeys.all, 'registration', registrationId, 'templates'] as const,
  templateSuggestions: (registrationId: number) =>
    [...ocrKeys.all, 'registration', registrationId, 'suggestions'] as const,
  containerPages: (containerId: number) =>
    [...ocrKeys.all, 'container', containerId, 'pages'] as const,
  pageTemplateSuggestions: (containerId: number) =>
    [...ocrKeys.all, 'container', containerId, 'page-suggestions'] as const,
  // Nested under the container key on purpose: the live hook's invalidation
  // of the container key refreshes every requested page too.
  pageTemplateSuggestion: (containerId: number, pageId: number) =>
    [...ocrKeys.pageTemplateSuggestions(containerId), 'page', pageId] as const,
  // Nested too, so a regenerate's invalidation re-reads what is cached.
  cachedPageTemplateSuggestions: (containerId: number) =>
    [...ocrKeys.pageTemplateSuggestions(containerId), 'cached'] as const,
  suggestionRuns: (registrationId: number) =>
    [
      ...ocrKeys.all,
      'registration',
      registrationId,
      'suggestion-runs'
    ] as const,
  containerSuggestionRuns: (containerId: number) =>
    [...ocrKeys.all, 'container', containerId, 'suggestion-runs'] as const,
  suggestionRun: (runId: number) =>
    [...ocrKeys.all, 'suggestion-run', runId] as const
};

// Hard ceiling on the suggestion polling loop, measured from the first
// 'pending' payload per registration. A fetch counter can't do this job:
// it never resets, so a re-opened dialog (or a later fresh suggestion
// cycle) would inherit an exhausted counter and never poll again. Cleared
// the moment the payload leaves 'pending' or the user explicitly
// re-requests suggestions.
const SUGGESTION_POLL_WINDOW_MS = 2 * 60 * 1000;
// Per-page match ranking is ~7s a page in windows of 10; 5 min covers ~400
// pages. Separate from the pool window above, which ranks once per document.
const PAGE_SUGGESTION_POLL_WINDOW_MS = 5 * 60 * 1000;
// Push-first delivery: while the WebSocket is healthy the backend's
// ready event patches the cache (use-template-suggestions-live), so the
// poll is just a slow safety net against a missed broadcast. When the
// socket is down, polling is the only delivery channel left — go fast.
const SUGGESTION_SAFETY_POLL_MS = 15_000;
const SUGGESTION_FALLBACK_POLL_MS = 3_000;
const suggestionPollStartedAt = new Map<number, number>();

/** True when the poll loop gave up while the backend still said 'pending'. */
export function suggestionPollingTimedOut(registrationId: number): boolean {
  const startedAt = suggestionPollStartedAt.get(registrationId);
  return (
    startedAt !== undefined &&
    Date.now() - startedAt > SUGGESTION_POLL_WINDOW_MS
  );
}

/** Restart the polling window — call when the user re-requests suggestions. */
export function resetSuggestionPollWindow(registrationId: number): void {
  suggestionPollStartedAt.delete(registrationId);
}

// Per-page suggestion polling window — same shape as the registration-level
// one above, keyed by container id.
const pagePollStartedAt = new Map<number, number>();

/** True when the per-page poll loop gave up while still 'pending'. */
export function pageSuggestionPollingTimedOut(containerId: number): boolean {
  const startedAt = pagePollStartedAt.get(containerId);
  return (
    startedAt !== undefined &&
    Date.now() - startedAt > PAGE_SUGGESTION_POLL_WINDOW_MS
  );
}

// Single-page ranking poll windows, keyed by page id.
const singlePagePollStartedAt = new Map<number, number>();

/** True when one page's poll loop gave up while still 'pending'. */
export function singlePageSuggestionPollingTimedOut(pageId: number): boolean {
  const startedAt = singlePagePollStartedAt.get(pageId);
  return (
    startedAt !== undefined &&
    Date.now() - startedAt > PAGE_SUGGESTION_POLL_WINDOW_MS
  );
}

/** Restart one page's polling window — call on every request for it. */
export function resetSinglePageSuggestionPollWindow(pageId: number): void {
  singlePagePollStartedAt.delete(pageId);
}

/** Restart the per-page polling window — call when the user re-opens the tab. */
export function resetPageSuggestionPollWindow(containerId: number): void {
  pagePollStartedAt.delete(containerId);
}

export const ocrQueries = {
  containers: (filters: OcrContainersListParams = {}) =>
    queryOptions({
      queryKey: ocrKeys.containerList(filters),
      queryFn: () => ocrApi.containers(filters),
      placeholderData: keepPreviousData,
      // The status badges here are advanced by the page-batch workers, not by a
      // mutation. `ocrKeys.containers()` is invalidated by the live hooks, but
      // none of them mount on the listing — and the one global subscriber
      // (`ActiveOcrJobsBadge` → `useOcrUserChannel`) is commented out of the
      // header — so mount refetch is the only thing keeping this page honest.
      staleTime: STALE_TIME.live
    }),

  containerResults: (containerId: number) =>
    queryOptions({
      queryKey: ocrKeys.containerResults(containerId),
      queryFn: () => ocrApi.containerResults(containerId),
      enabled: containerId > 0
    }),

  /**
   * The container's identity for the results-page header. `STALE_TIME.live`
   * because `qa_stage` is advanced by the QA endpoints and the template pool
   * by the registration screens — neither of which this page's mutations mark
   * stale, so mount refetch is what keeps the header honest.
   */
  container: (containerId: number) =>
    queryOptions({
      queryKey: ocrKeys.container(containerId),
      queryFn: () => ocrApi.containerSummary(containerId),
      staleTime: STALE_TIME.live,
      enabled: containerId > 0
    }),

  containerHistory: (containerId: number) =>
    queryOptions({
      queryKey: ocrKeys.containerHistory(containerId),
      queryFn: () => ocrApi.containerHistory(containerId),
      enabled: containerId > 0
    }),

  result: (resultId: number) =>
    queryOptions({
      queryKey: ocrKeys.result(resultId),
      queryFn: () => ocrApi.getResult(resultId),
      enabled: resultId > 0
    }),

  activeJobs: () =>
    queryOptions({
      queryKey: ocrKeys.activeJobs(),
      queryFn: () => ocrApi.activeJobs()
    }),

  registrationTemplates: (registrationId: number) =>
    queryOptions({
      queryKey: ocrKeys.registrationTemplates(registrationId),
      queryFn: () => ocrApi.listRegistrationTemplates(registrationId),
      enabled: registrationId > 0
    }),

  templateSuggestions: (registrationId: number, enabled = true) =>
    queryOptions({
      queryKey: ocrKeys.templateSuggestions(registrationId),
      queryFn: () => ocrApi.suggestTemplates(registrationId),
      enabled: enabled && registrationId > 0,
      // Suggestion calls hit the model provider — cache for 5 min so re-opening the
      // dialog doesn't re-bill. The backend also caches for 24h.
      staleTime: 5 * 60 * 1000,
      // Async backend contract: 'pending' means the suggestion job is still
      // computing. Primary delivery is the WebSocket ready event; this poll
      // is the safety net / socket-down fallback. Time-boxed (~2 min) so a
      // dead queue worker doesn't poll forever; the manual picker stays
      // fully usable throughout.
      refetchInterval: (query) => {
        const payload = query.state.data?.data;
        if (!payload || payload.status !== 'pending') {
          suggestionPollStartedAt.delete(registrationId);
          return false;
        }
        const startedAt =
          suggestionPollStartedAt.get(registrationId) ?? Date.now();
        suggestionPollStartedAt.set(registrationId, startedAt);
        if (Date.now() - startedAt > SUGGESTION_POLL_WINDOW_MS) {
          return false;
        }
        return getShouldFallback()
          ? SUGGESTION_FALLBACK_POLL_MS
          : SUGGESTION_SAFETY_POLL_MS;
      }
    }),

  // Manual picker's page list — no model call, so it renders instantly. Powers the
  // "Assign per page" panel now that AI ranking is disabled ("coming soon").
  containerPages: (containerId: number, enabled = true) =>
    queryOptions({
      queryKey: ocrKeys.containerPages(containerId),
      queryFn: () => ocrApi.getContainerPages(containerId),
      enabled: enabled && containerId > 0,
      staleTime: 5 * 60 * 1000
    }),

  pageTemplateSuggestions: (containerId: number, enabled = true) =>
    queryOptions({
      queryKey: ocrKeys.pageTemplateSuggestions(containerId),
      queryFn: () => ocrApi.getPageTemplateSuggestions(containerId),
      enabled: enabled && containerId > 0,
      // Per-page classification hits the model provider — cache 5 min so re-opening the
      // tab doesn't re-bill. The backend also caches for 24h.
      staleTime: 5 * 60 * 1000,
      // Same async contract as templateSuggestions: 'pending' polls until the
      // backend job caches a result; WebSocket push is primary, this is the
      // safety net / socket-down fallback, time-boxed (~5 min).
      refetchInterval: (query) => {
        const payload = query.state.data?.data;
        if (!payload || payload.status !== 'pending') {
          pagePollStartedAt.delete(containerId);
          return false;
        }
        const startedAt = pagePollStartedAt.get(containerId) ?? Date.now();
        pagePollStartedAt.set(containerId, startedAt);
        if (Date.now() - startedAt > PAGE_SUGGESTION_POLL_WINDOW_MS) {
          return false;
        }
        return getShouldFallback()
          ? SUGGESTION_FALLBACK_POLL_MS
          : SUGGESTION_SAFETY_POLL_MS;
      }
    }),

  /**
   * What the dialog finds on open: a cached ranking, or the pages ranked one
   * at a time. Never starts a ranking — that stays the operator's call.
   */
  cachedPageTemplateSuggestions: (containerId: number) =>
    queryOptions({
      queryKey: ocrKeys.cachedPageTemplateSuggestions(containerId),
      queryFn: () =>
        ocrApi.getPageTemplateSuggestions(containerId, { cachedOnly: true }),
      enabled: containerId > 0,
      staleTime: 0
    }),

  pageTemplateSuggestion: (
    containerId: number,
    pageId: number,
    enabled = true
  ) =>
    queryOptions({
      queryKey: ocrKeys.pageTemplateSuggestion(containerId, pageId),
      queryFn: () => ocrApi.getPageTemplateSuggestion(containerId, pageId),
      enabled: enabled && containerId > 0 && pageId > 0,
      staleTime: 5 * 60 * 1000,
      // One page is one model call (~30-50s); poll as a safety net behind the
      // socket push, time-boxed like the container poll.
      refetchInterval: (query) => {
        const payload = query.state.data?.data;
        if (!payload || payload.status !== 'pending') {
          singlePagePollStartedAt.delete(pageId);
          return false;
        }
        const startedAt = singlePagePollStartedAt.get(pageId) ?? Date.now();
        singlePagePollStartedAt.set(pageId, startedAt);
        if (Date.now() - startedAt > PAGE_SUGGESTION_POLL_WINDOW_MS) {
          return false;
        }
        return getShouldFallback()
          ? SUGGESTION_FALLBACK_POLL_MS
          : SUGGESTION_SAFETY_POLL_MS;
      }
    }),

  suggestionRuns: (registrationId: number, enabled = true) =>
    queryOptions({
      queryKey: ocrKeys.suggestionRuns(registrationId),
      queryFn: () => ocrApi.getSuggestionRuns(registrationId),
      enabled: enabled && registrationId > 0
    }),

  containerSuggestionRuns: (containerId: number, enabled = true) =>
    queryOptions({
      queryKey: ocrKeys.containerSuggestionRuns(containerId),
      queryFn: () => ocrApi.getContainerSuggestionRuns(containerId),
      enabled: enabled && containerId > 0
    }),

  suggestionRun: (runId: number, enabled = true) =>
    queryOptions({
      queryKey: ocrKeys.suggestionRun(runId),
      queryFn: () => ocrApi.getSuggestionRun(runId),
      enabled: enabled && runId > 0,
      staleTime: 5 * 60 * 1000
    })
};
